import client from 'app/client';

export interface AccessMember {
  accessId: string;
  userId: string;
  displayName: string;
  email: string;
  /** Owner of the firm (shown as "Gérant" in governance): team, portfolio, bricks. */
  isOwner: boolean;
  isActive: boolean;
  isPrincipal: boolean;
  dateAdhesion: string;
  /**
   * Manager in the legal sense: the natural person entitled to sign the firm's
   * direct-debit mandates. Distinct from `isOwner`.
   */
  isGerant?: boolean;
  /** The manager's mobile, the one that receives the signature code. */
  phone?: string | null;
}

/** A firm manager entered without an account (company creation), to attach to an access. */
export interface GerantWithoutAccess {
  collaboratorId: string;
  displayName: string;
  email?: string | null;
  phone?: string | null;
}

export interface GetAccessesResponse {
  personneMoraleId: string;
  collaborators: AccessMember[];
  gerantsWithoutAccess?: GerantWithoutAccess[];
}

export interface DeclareGerantCommand {
  /** Civility of the natural person to create from the account. Ignored when a manager is attached. */
  civiliteId?: string | null;
  /** The manager's mobile (06/07). Optional here; the mandate card enforces it on its side. */
  phone?: string | null;
  /** Existing manager without an account, to attach to this access. */
  collaboratorId?: string | null;
}

export interface OperationResponse {
  isSuccess: boolean;
  errorMessage?: string;
  id?: string;
}

/**
 * Liste les membres actifs d'une personne morale (Owner only).
 */
export async function getPersonneMoraleMembers(
  personneMoraleId: string,
): Promise<GetAccessesResponse> {
  const { data } = await client.get<GetAccessesResponse>(
    `/backoffice/personnes-morales/${personneMoraleId}/access`,
  );
  return data;
}

/**
 * Ajoute un user déjà inscrit comme collaborateur de la personne morale (gérant only).
 * MVP : le user doit déjà avoir un compte et ne pas appartenir à une autre PM.
 */
export async function addCollaborator(
  personneMoraleId: string,
  email: string,
  isOwner: boolean,
): Promise<OperationResponse> {
  const { data } = await client.post<OperationResponse>(
    `/backoffice/personnes-morales/${personneMoraleId}/access`,
    { email, isOwner },
  );
  return data;
}

/**
 * Declares the access as the firm's (legal) manager — signatory of its mandates. Owner
 * only. Creates the natural person from the account, or attaches a manager entered without one.
 */
export async function declareGerant(
  accessId: string,
  command: DeclareGerantCommand,
): Promise<OperationResponse> {
  const { data } = await client.put<OperationResponse>(
    `/backoffice/personnes-morales/access/${accessId}/gerant`,
    command,
  );
  return data;
}

/** Removes the manager role from the access: the collaborator is closed, the access stays. */
export async function removeGerant(accessId: string): Promise<OperationResponse> {
  const { data } = await client.delete<OperationResponse>(
    `/backoffice/personnes-morales/access/${accessId}/gerant`,
  );
  return data;
}

export interface CreateAndAddCollaboratorResponse extends OperationResponse {
  data?: {
    temporaryPassword: string;
    userId: string;
  };
}

/**
 * Crée un nouveau compte ABP minimal et l'ajoute comme collaborateur en une transaction.
 * Le mdp temporaire est généré côté back et retourné dans `data.temporaryPassword`.
 * L'Owner doit le communiquer manuellement au nouveau collaborateur.
 */
export async function createAndAddCollaborator(
  personneMoraleId: string,
  payload: { email: string; firstName: string; lastName: string; isOwner: boolean },
): Promise<CreateAndAddCollaboratorResponse> {
  const { data } = await client.post<CreateAndAddCollaboratorResponse>(
    `/backoffice/personnes-morales/${personneMoraleId}/access/create-and-add`,
    payload,
  );
  return data;
}

/**
 * Nomme ou rétrograde le gérant d'un cabinet (gérant only). Le dernier gérant
 * actif ne peut pas être rétrogradé (garde serveur).
 */
export async function setAccessOwner(
  accessId: string,
  isOwner: boolean,
): Promise<OperationResponse> {
  const { data } = await client.put<OperationResponse>(
    `/backoffice/personnes-morales/access/${accessId}/owner`,
    { isOwner },
  );
  return data;
}

export interface CollaboratorBrique {
  nom: string;
  description?: string | null;
}

export interface PlateformeDroitsSpecifiques {
  plateformeId: string;
  designation: string;
}

export interface GetCollaboratorBriquesResponse {
  accessId: string;
  abpUserId: string;
  isOwner: boolean;
  /** Briques globales du compte (catalogue uniquement). */
  briquesCompte: string[];
  /** Ce que l'Owner peut accorder/retirer : le profil du cabinet. */
  briquesAttribuables: CollaboratorBrique[];
  /** Plateformes où un admin a posé des droits spécifiques (override) : là-bas,
   * l'override REMPLACE les briques du compte — l'édition cabinet y est sans effet. */
  droitsSpecifiquesPlateformes?: PlateformeDroitsSpecifiques[];
}

/**
 * Briques d'un membre du cabinet, vues par l'Owner : l'état du compte et le
 * plafond attribuable (profil de la nature/sous-nature du cabinet).
 */
export async function getCollaboratorBriques(
  accessId: string,
): Promise<GetCollaboratorBriquesResponse> {
  const { data } = await client.get<GetCollaboratorBriquesResponse>(
    `/backoffice/personnes-morales/access/${accessId}/briques`,
  );
  return data;
}

/**
 * Pose l'ensemble EXACT des briques attribuables d'un membre (Owner only) —
 * la redistribution des rôles au sein du cabinet, membre par membre. Les
 * briques hors plafond (posées par un admin) ne sont pas touchées, l'auto-
 * édition et les comptes admin sont refusés côté serveur.
 */
export async function setCollaboratorBriques(
  accessId: string,
  briques: string[],
): Promise<OperationResponse> {
  const { data } = await client.put<OperationResponse>(
    `/backoffice/personnes-morales/access/${accessId}/briques`,
    { briques },
  );
  return data;
}

/**
 * Retire un membre de la personne morale (Owner only).
 * Bloqué si c'est le dernier Owner — promouvoir d'abord un autre membre.
 */
export async function removeAccess(accessId: string): Promise<OperationResponse> {
  const { data } = await client.delete<OperationResponse>(
    `/backoffice/personnes-morales/access/${accessId}`,
  );
  return data;
}

/**
 * Marque un Owner comme principal de la personne morale (Owner only).
 * L'ancien principal est démarqué automatiquement côté back.
 */
export async function promotePrincipalAccess(accessId: string): Promise<OperationResponse> {
  const { data } = await client.patch<OperationResponse>(
    `/backoffice/personnes-morales/access/${accessId}/promote-principal`,
  );
  return data;
}

export interface ResetPasswordResponse extends OperationResponse {
  data?: {
    temporaryPassword: string;
  };
}

/**
 * Réinitialise le mot de passe d'un membre (Owner only).
 * Le nouveau mot de passe temporaire est retourné dans `data.temporaryPassword`.
 */
export async function resetUserPassword(accessId: string): Promise<ResetPasswordResponse> {
  const { data } = await client.post<ResetPasswordResponse>(
    `/backoffice/personnes-morales/access/${accessId}/reset-password`,
  );
  return data;
}

/**
 * Supprime définitivement le compte ABP d'un membre (Owner only) : désactive l'accès
 * cabinet puis anonymise et soft-delete le compte. Bloqué si c'est le dernier Owner.
 */
export async function deleteUserAccount(accessId: string): Promise<OperationResponse> {
  const { data } = await client.delete<OperationResponse>(
    `/backoffice/personnes-morales/access/${accessId}/account`,
  );
  return data;
}
